import { Team } from "@entities";
import { teamRepo } from "@repositories";
import { Roles } from "@shared/Roles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { remult } from "remult";
import { useAlert } from "../contexts/AlertProvider";
import { useOrganization } from "../contexts/OrganizationProvider";
import { ADMIN_LIST_QUERY_OPTIONS } from "../helpers/admin-list-query";
import { useUser } from "../contexts/UserProvider";

export function useCommunities() {
  const { currentUser } = useUser();
  const { currOrganization } = useOrganization();
  const {
    data: communities,
    refetch,
    ...query
  } = useQuery({
    queryKey: ["communities", currentUser?.id],
    queryFn: () => {
      return teamRepo.find({
        where: {
          $and: [
            { organization: currOrganization! },
            remult.isAllowed(Roles.Admins)
              ? {}
              : Team.isTeamOrAnySubTeamUnderThisTeamManagedBy({
                  manager: currentUser!.id,
                }),
          ],
        },
        orderBy: { name: "asc" },
        include: {
          subTeams: false,
          parentTeam: {
            include: {
              team: true,
            },
          },
        },
      });
    },
    enabled: !!currOrganization && !!currentUser,
    ...ADMIN_LIST_QUERY_OPTIONS,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const communityTypeIndex = (communities ?? []).findIndex(
    (comm) => comm.isMainOrganizationTeam,
  );

  if (communityTypeIndex > -1) {
    const [communityTypeCommunity] = communities!.splice(communityTypeIndex, 1);
    communities!.unshift(communityTypeCommunity);
  }

  return { communities, refetch, ...query };
}

export function useSubCommunities() {
  const { communities } = useCommunities();
  const { data: subCommunities = [], ...query } = useQuery({
    queryKey: ["sub-communities", communities?.map((comm) => comm.id)],
    queryFn: () => communities?.filter((comm) => !comm.isMainOrganizationTeam),
    enabled: !!communities,
  });

  return {
    subCommunities,
    ...query,
  };
}

export function useDeleteTeam() {
  const { setSeverity, setAlertText } = useAlert();
  const { communities = [] } = useCommunities();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-team"],
    mutationFn: async (options: { teamIds: string[] }) => {
      const { teamIds } = options;
      const fallbackTeamId = communities?.find(
        (com) => com.isMainOrganizationTeam,
      )?.id;
      return Team.deleteTeamsAndReassignOpportunities({
        teamIds,
        fallbackTeamId: fallbackTeamId ?? null,
      });
    },
    onSuccess: (data) => {
      setSeverity("success");
      setAlertText(
        `${data.teamIds.length > 1 ? "הפרויקטים" : "הפרויקט"} ${data.teamIds.length > 1 ? "נמחקו" : "נמחקה"} בהצלחה`,
      );
      void queryClient.invalidateQueries({ queryKey: ["communities"] });
    },
    onError: (error) => {
      setSeverity("error");
      setAlertText(
        error instanceof Error
          ? error.message
          : "קרתה תקלה, נסו שוב מאוחר יותר",
      );
    },
  });
}
